<template>
  <div
    class="studio-lesson-media-card d-flex flex-row align-items-start justify-content-start flex-nowrap"
    :class="{ pointer: hasAccess }"
    v-bind="$attrs"
    @mouseover="hover = true"
    @mouseleave="hover = false"
  >
    <div
      v-tooltip="
        (media.locked || !canViewProductContent) && {
          content: getCantViewProductReasonText,
          delay: 100,
          trigger: 'manual',
          show: hover,
          placement: 'top',
          container: 'body',
        }
      "
    >
      <HMFixedAspectRatioContainer>
        <MediaThumbnail
          :media="media"
          :isLocked="media.locked || !canViewProductContent || !hasAccess"
          :mediaIconsEnabled="mediaIconsEnabled"
          :mediaTimeEnabled="false"
        />
      </HMFixedAspectRatioContainer>
    </div>
    <div
      class="ms-3 description d-flex flex-column align-items-start justify-content-start"
    >
      <div class="d-flex flex-row align-items-start justify-content-start">
        <HMInstructorsStack
          class="me-2 d-none d-md-block"
          v-if="media.instructor"
          :instructors="getInstructors(media.instructor)"
          size="24px"
        />
        <div class="m-0">
          <HMTextClamp autoresize :max-lines="2" class="h4 media-name" :text="media.title" />
        </div>
      </div>
      <div
        class="d-flex align-items-center justify-content-start flex-wrap text-truncate mw-100 mt-1"
      >
        <HMInstructorsStack
          class="me-2 d-block d-md-none mb-1"
          v-if="media.instructor"
          :instructors="getInstructors(media.instructor)"
          size="24px"
        />
        <HMTag
          class="smallest me-2 mb-1"
          :title="tag.option_name"
          v-for="tag in getMediaTags"
          :key="tag.id"
        />
        <HMTag
          class="smallest mb-1"
          v-if="getRemainingMediaTagsText"
          :title="getRemainingMediaTagsText"
          v-tooltip="{
            content: getCutOffMediaTagsHtml,
            html: true,
            delay: 100,
            offset: 5,
            placement: 'bottom',
            container: 'body',
          }"
        />
      </div>
      <ClientOnly>
        <HMTextClamp
          autoresize
          :max-lines="6"
          class="mt-2 d-none d-lg-block text-wrap text-break"
          v-if="media.description || media.description_new"
          :text="strip(media.description, media.description_new)"
        />
        <HMTextClamp
          autoresize
          :max-lines="2"
          class="mt-2 d-none d-md-block d-lg-none text-wrap text-break"
          v-if="media.description || media.description_new"
          :text="strip(media.description, media.description_new)"
        />
      </ClientOnly>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { formatDate } from '@/utils/format';
import { renderHtmlFromEditorJS } from '@/utils/utils';
import MediaThumbnail from '@/components/products/MediaThumbnail.vue';
import { useProductStore, useStudioStore } from '@/stores';

export default {
  name: 'StudioLessonMediaCard',
  components: { MediaThumbnail },
  props: {
    media: {
      type: Object as () => ProductMediaResponse,
      required: true,
    },
    hasAccess: {
      type: Boolean,
    },
  },
  computed: {
    ...mapState(useProductStore, {
      product: 'product',
      canViewProductContent: 'canViewProductContent',
      cantViewProductContentReason: 'cantViewProductContentReason',
    }),
    ...mapState(useStudioStore, {
      mediaIconsEnabled: 'mediaIconsEnabled',
    }),
    getCantViewProductReasonText() {
      if (this.canViewProductContent) return null;
      switch (this.cantViewProductContentReason) {
        case 'unauthenticated':
          return this.$t(
            'PRODUCTS.TEXT.PRODUCT_NO_ACCESS_USER_NOT_AUTHENTICATED'
          );
        case 'not_purchased':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_NOT_PURCHASED');
        case 'availability_expired':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_EXPIRED', {
            date: formatDate(
              this.product.can_view_content.end_date,
              'date-time'
            ),
          });
        case 'drip_expired':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_EXPIRED', {
            date: formatDate(
              this.product.can_view_content.end_date,
              'date-time'
            ),
          });
        case 'not_yet_available':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_NOT_YET_AVAILABLE', {
            date: formatDate(
              this.product.can_view_content.start_date,
              'date-time'
            ),
          });
      }
      return '';
    },
    getMediaTags() {
      const mediaOptionsArray = this.media.options.slice();
      return mediaOptionsArray.splice(0, 2);
    },
    getCutOffMediaTagsHtml() {
      const mediaOptionsArray = this.media.options.slice();
      return mediaOptionsArray
        .splice(2)
        .map(option => option.option_name)
        .join('<br/>');
    },
    getRemainingMediaTagsText() {
      if (this.media.options.length < 3) return null;
      return `+ ${this.media.options.length - 2}`;
    },
  },
  data() {
    return {
      hover: false,
    };
  },
  methods: {
    strip(html, json) {
      let useHtml = html;
      if (json) {
        useHtml = renderHtmlFromEditorJS(json);
      }

      
      if(DOMParser) {
        const doc = new DOMParser().parseFromString(useHtml, 'text/html');
        return doc.body.textContent || '';
      }
    },
    getInstructors(instructor) {
      return instructor ? [instructor] : [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
.studio-lesson-media-card {
  &:hover {
    :deep(.image) {
      box-shadow: 0 12px 12px -6px $medium-gray;
      transform: translateY(-16px);
    }
    .media-name {
      color: var(--brand-color);
    }
  }

  position: relative;
  border-radius: 6px;

  .outer-container {
    border-radius: 6px;
  }

  .media-name {
    color: $charcoal;
  }

  .heart {
    color: $coral;
  }

  .dot {
    margin-right: 0.25rem;
    margin-top: 0.3rem;
    font-size: 0.5rem;
  }

  :deep(.ratio-16x9) {
    overflow: visible;
    flex-shrink: 0;
    width: 300px;
    @include media-breakpoint-down(md) {
      width: 200px;
    }
    @include media-breakpoint-down(sm) {
      width: 100px;
    }
  }
}
</style>
